import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getProblemStatement, updateProblemStatement } from '../api/problems';
import { listCampaigns, syncCampaign } from '../api/meta';
import { useAppStore } from '../stores/app';
import { deriveStatus } from '../lib/status';
import { StatusBadge } from '../components/StatusBadge';

export function ProblemDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const sessionId = useAppStore((s) => s.activeSessionId);
  const qc = useQueryClient();

  const { data: problem, isLoading } = useQuery({
    queryKey: ['problem', id],
    queryFn: () => getProblemStatement(id!),
    enabled: !!id,
  });

  const { data: campaigns } = useQuery({
    queryKey: ['campaigns', sessionId],
    queryFn: () => listCampaigns(sessionId!),
    enabled: !!sessionId,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof updateProblemStatement>[1]) =>
      updateProblemStatement(id!, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['problem', id] });
      qc.invalidateQueries({ queryKey: ['problems'] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: (campaignId: string) => syncCampaign(campaignId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  if (isLoading || !problem) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-brand-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const status = deriveStatus(problem);
  const relatedCampaigns = (campaigns ?? []).filter(
    (c) => c.problem_statement_id === problem.id
  );

  return (
    <div className="space-y-6 animate-fadeIn max-w-4xl">
      {/* Back */}
      <button
        onClick={() => navigate('/problems')}
        className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1"
      >
        ← Zurück zur Liste
      </button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{problem.topic}</h1>
          <StatusBadge status={status} size="md" />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => updateMutation.mutate({ validated: 1 })}
            disabled={updateMutation.isPending}
            className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            ✅ Validieren
          </button>
          <button
            onClick={() => updateMutation.mutate({ validated: -1 })}
            disabled={updateMutation.isPending}
            className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            ❌ Ablehnen
          </button>
        </div>
      </div>

      {/* Statement */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Problem-Statement</h2>
        <p className="text-gray-800 whitespace-pre-wrap">{problem.statement}</p>
        {problem.intro && (
          <p className="text-sm text-gray-500 mt-3 italic">{problem.intro}</p>
        )}
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-xs text-gray-500">CTR</p>
          <p className="text-xl font-bold text-gray-900">
            {problem.ctr != null ? `${(problem.ctr * 100).toFixed(2)}%` : '–'}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-xs text-gray-500">Priorität</p>
          <p className="text-xl font-bold text-gray-900">{problem.priority ?? '–'}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-xs text-gray-500">Review Status</p>
          <p className="text-xl font-bold text-gray-900">{problem.review_status}</p>
        </div>
      </div>

      {/* Related Campaigns */}
      {relatedCampaigns.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">
            Zugehörige Kampagnen ({relatedCampaigns.length})
          </h2>
          <div className="space-y-3">
            {relatedCampaigns.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between border border-gray-100 rounded-lg p-3"
              >
                <div>
                  <p className="text-sm font-medium text-gray-800">{c.headline}</p>
                  <p className="text-xs text-gray-500">
                    Status: {c.status} · Impressions: {c.impressions ?? 0} · Clicks: {c.clicks ?? 0} ·
                    CTR: {c.ctr != null ? `${c.ctr.toFixed(2)}%` : '–'}
                  </p>
                </div>
                <button
                  onClick={() => syncMutation.mutate(c.id)}
                  disabled={syncMutation.isPending}
                  className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-600"
                >
                  🔄 Sync
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
